import { useEffect, useState, type FormEvent } from "react";
import { useTranslate } from "@refinedev/core";
import { PasswordField, Button, LockIcon } from "@fawkes/ui";

import "./LoginPage.css";
import "./SetPasswordPage.css";

// Same base-URL convention as dataProvider/authProvider: the Vite dev server proxies /api to the API.
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

type Status = "idle" | "submitting" | "success";

/**
 * Anonymous `/set-password` page — the React recreation of the Jmix Vaadin `SetPasswordView`. The
 * plaintext reset token rides in the URL fragment (`/set-password#<token>`), never a query parameter,
 * so it stays browser-side and never reaches server logs; it's pulled once on mount and stripped from
 * the address bar. The new password is posted to `POST /api/auth/set-password` (anonymous, rate-limited
 * server-side). Anti-oracle parity: every expired/used/unknown-token failure shows the SAME message; a
 * weak password — the user's own input — gets its own hint.
 */
export function SetPasswordPage() {
  const t = useTranslate();
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fragment = globalThis.location?.hash?.slice(1) ?? "";
    if (fragment) {
      setToken(decodeURIComponent(fragment));
      // Strip the fragment so the token doesn't linger in history / clipboard-copy of the address.
      const { pathname, search } = globalThis.location;
      globalThis.history?.replaceState?.(null, "", pathname + search);
    }
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!password || password !== confirm) {
      setError(t("setPassword.error.confirmMismatch", undefined, "De wachtwoorden komen niet overeen."));
      return;
    }
    if (!token) {
      setError(t("setPassword.error.uniform", undefined, "De link is verlopen of ongeldig."));
      return;
    }

    setStatus("submitting");
    let response: Response;
    try {
      response = await fetch(`${API_URL}/auth/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ token, password }),
      });
    } catch {
      setStatus("idle");
      setError(t("setPassword.error.uniform", undefined, "De link is verlopen of ongeldig."));
      return;
    }

    if (response.status === 204) {
      setStatus("success");
      return;
    }
    setStatus("idle");
    // Drop the token so a retry can't replay a server-seen value.
    setToken(null);
    if (response.status === 422) {
      setError(t("setPassword.error.weakPassword", undefined,
        "Kies een sterker wachtwoord (minimaal 8 tekens)."));
      return;
    }
    // 400 (invalid/expired/used) / 429 (rate-limited) / anything else → one uniform message.
    setError(t("setPassword.error.uniform", undefined, "De link is verlopen of ongeldig."));
  };

  if (status === "success") {
    return (
      <div className="login-main">
        <div className="login-card set-password-card">
          <div className="login-header">
            <img className="login-title-icon" src="/login/login-lock.png" alt="" aria-hidden="true" />
            <h1 className="login-title">{t("setPassword.title", undefined, "Wachtwoord instellen")}</h1>
          </div>
          <p className="set-password-success" role="status">
            {t("setPassword.success", undefined, "Je wachtwoord is ingesteld. Je kunt nu inloggen.")}
          </p>
          <Button
            type="button"
            variant="primary"
            className="login-submit"
            onClick={() => globalThis.location?.assign?.("/")}
          >
            {t("setPassword.toLogin", undefined, "Naar inloggen")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-main">
      <form className="login-card set-password-card" onSubmit={onSubmit}>
        <div className="login-header">
          <img className="login-title-icon" src="/login/login-lock.png" alt="" aria-hidden="true" />
          <h1 className="login-title">{t("setPassword.title", undefined, "Wachtwoord instellen")}</h1>
        </div>

        <p className="set-password-intro">
          {t("setPassword.intro", undefined, "Kies een nieuw wachtwoord voor je account.")}
        </p>

        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}

        <PasswordField
          label={t("setPassword.newPassword", undefined, "Nieuw wachtwoord")}
          icon={<LockIcon />}
          autoComplete="new-password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          revealLabel={t("login.showPassword", undefined, "Wachtwoord tonen")}
          hideLabel={t("login.hidePassword", undefined, "Wachtwoord verbergen")}
        />
        <PasswordField
          label={t("setPassword.confirmPassword", undefined, "Bevestig wachtwoord")}
          icon={<LockIcon />}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          revealLabel={t("login.showPassword", undefined, "Wachtwoord tonen")}
          hideLabel={t("login.hidePassword", undefined, "Wachtwoord verbergen")}
        />

        <Button type="submit" variant="primary" disabled={status === "submitting"} className="login-submit">
          {t("setPassword.submit", undefined, "Wachtwoord instellen")}
        </Button>
      </form>
    </div>
  );
}
